import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Check,
  ArrowRight,
  HelpCircle,
  Shield,
  CreditCard,
  RefreshCcw,
  Zap,
  AlertCircle
} from 'lucide-react';
import { pricingPlans } from '../data/mockData';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';

// ThriveCart checkout URLs
const THRIVECART_URLS = {
  trial: 'https://dchf.thrivecart.com/valoramap-trial/',
  founding: 'https://dchf.thrivecart.com/valoramap-founding-members/',
  premium: 'https://dchf.thrivecart.com/valoramap-premium/'
};

const PricingPage = () => {
  const [searchParams] = useSearchParams();
  const [accessMessage, setAccessMessage] = useState(null);

  // Check for redirect reason (e.g., no subscription)
  useEffect(() => {
    const reason = searchParams.get('reason');
    if (reason === 'no_subscription') {
      setAccessMessage('Votre abonnement n\'est pas actif — Débloquez l\'accès complet au screener');
    }
  }, [searchParams]);

  const faqs = [
    {
      question: "Qu'est-ce qui est inclus dans l'essai à 9 CHF ?",
      answer: "L'essai vous donne un accès complet à toutes les fonctionnalités pendant 7 jours : screener sur les 6 marchés, données fondamentales, exports CSV, alertes, et guides d'investissement. Si vous êtes satisfait, vous passez automatiquement au plan Premium ou Founding de votre choix. Sinon, vous êtes remboursé."
    },
    {
      question: "Qu'est-ce que le plan Founding Member ?",
      answer: "Le plan Founding Member est réservé aux 50 premiers utilisateurs. Pour 279 CHF/an (au lieu de 399 CHF), vous bénéficiez de toutes les fonctionnalités Premium, avec un prix garanti à vie. Même si nos tarifs augmentent, votre abonnement restera à 279 CHF/an. Vous avez aussi accès aux beta features et aux AMA privés."
    },
    {
      question: "Quels marchés sont couverts ?",
      answer: "Nous couvrons 6 marchés émergents : Vietnam (Ho Chi Minh Stock Exchange), Inde (National Stock Exchange), Philippines (PSE), Indonésie (IDX), Pologne (Warsaw Stock Exchange), et Corée du Sud (Korea Exchange). Plus de 6 200 actions au total."
    },
    {
      question: "D'où viennent les données ?",
      answer: "Nos données proviennent de différentes sources utilisées par les institutions financières internationales que nous filtrons, compilons et enrichissons. Vous avez accès aux bilans, comptes de résultat, flux de trésorerie, et 50+ ratios fondamentaux avec un historique de 18 à 31 ans selon les marchés."
    },
    {
      question: "Puis-je annuler à tout moment ?",
      answer: "Oui, vous pouvez annuler votre abonnement à tout moment depuis votre compte. Il n'y a pas d'engagement. Si vous annulez pendant l'essai, vous êtes remboursé intégralement."
    },
    {
      question: "Les données sont-elles en temps réel ?",
      answer: "Les données de prix sont mises à jour quotidiennement (fin de journée). Les données fondamentales sont mises à jour trimestriellement après la publication des résultats. Pour l'investissement value long-terme, cette fréquence est idéale."
    }
  ];

  const guarantees = [
    {
      icon: Shield,
      title: 'Satisfait ou remboursé',
      description: 'Essai de 7 jours avec remboursement si non satisfait'
    },
    {
      icon: CreditCard,
      title: 'Paiement sécurisé',
      description: 'Transactions cryptées via Stripe'
    },
    {
      icon: RefreshCcw,
      title: 'Sans engagement',
      description: 'Annulation possible à tout moment'
    }
  ];

  // Redirect to ThriveCart checkout
  const getCheckoutUrl = (planId) => {
    return THRIVECART_URLS[planId] || THRIVECART_URLS.trial;
  };

  return (
    <div className="pt-24 pb-16 px-4 sm:px-6 lg:px-8">
      <div className="max-w-6xl mx-auto">
        {/* Access Denied Message */}
        {accessMessage && (
          <div className="mb-8 p-4 bg-amber-500/10 border border-amber-500/30 rounded-xl flex items-center gap-3">
            <AlertCircle className="w-6 h-6 text-amber-400 flex-shrink-0" />
            <p className="text-amber-200 font-medium">{accessMessage}</p>
          </div>
        )}

        {/* Header */}
        <div className="text-center mb-12">
          <Badge className="bg-emerald-500/10 text-emerald-400 border-emerald-500/20 mb-4">
            Offre de lancement
          </Badge>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
            Un prix simple, une valeur immense
          </h1>
          <p className="text-lg text-gray-400 max-w-2xl mx-auto">
            Commencez avec un essai à 9 CHF pour tester toutes les fonctionnalités, 
            puis choisissez le plan qui vous convient.
          </p>
        </div>

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-3 gap-6 mb-16">
          {pricingPlans.map((plan) => (
            <Card 
              key={plan.id} 
              className={`relative bg-white/5 border-white/10 ${
                plan.popular ? 'ring-2 ring-emerald-500 border-emerald-500/30' : ''
              }`}
            >
              {plan.popular && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                  <Badge className="bg-emerald-500 text-white border-0 px-4 py-1">
                    Le plus populaire
                  </Badge>
                </div>
              )}
              {plan.limited && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                  <Badge className="bg-amber-500 text-white border-0 px-4 py-1">
                    {plan.remaining} places restantes
                  </Badge>
                </div>
              )}
              <CardContent className="p-8 pt-10">
                <div className="mb-6">
                  <h3 className="text-xl font-bold text-white mb-1">
                    {plan.name}
                  </h3>
                  <p className="text-sm text-gray-400">{plan.description}</p>
                </div>

                <div className="mb-6">
                  <div className="flex items-baseline gap-1">
                    <span className="text-5xl font-bold text-white">{plan.price}</span>
                    <span className="text-xl text-gray-300">CHF</span>
                    <span className="text-gray-400">/{plan.period}</span>
                  </div>
                  {plan.id === 'trial' && (
                    <p className="text-sm text-gray-500 mt-2">
                      Puis 399 CHF ou 279 CHF/an selon votre choix
                    </p>
                  )}
                </div>

                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5" />
                      <span className="text-gray-300 text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>

                <a 
                  href={getCheckoutUrl(plan.id)}
                  data-testid={`subscribe-${plan.id}`}
                  className={`w-full h-12 flex items-center justify-center rounded-md font-medium transition-colors ${
                    plan.popular || plan.id === 'trial'
                      ? 'bg-emerald-500 hover:bg-emerald-600 text-white' 
                      : 'bg-white/10 hover:bg-white/15 text-white'
                  }`}
                >
                  {plan.cta}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </a>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Guarantees */}
        <div className="grid md:grid-cols-3 gap-6 mb-16">
          {guarantees.map((guarantee, index) => (
            <div 
              key={index} 
              className="flex items-center gap-4 p-6 rounded-xl bg-white/5 border border-white/10"
            >
              <div className="w-12 h-12 rounded-xl bg-emerald-500/10 flex items-center justify-center shrink-0">
                <guarantee.icon className="w-6 h-6 text-emerald-400" />
              </div>
              <div>
                <h4 className="font-medium text-white mb-1">{guarantee.title}</h4>
                <p className="text-sm text-gray-400">{guarantee.description}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Comparison Note */}
        <Card className="bg-gradient-to-r from-emerald-500/10 to-teal-500/10 border-emerald-500/20 mb-16">
          <CardContent className="p-6 sm:p-8">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="flex items-center gap-4">
                <Zap className="w-10 h-10 text-emerald-400 shrink-0" />
                <div>
                  <h3 className="text-lg font-bold text-white mb-1">
                    Pourquoi VALORAMAP ?
                  </h3>
                  <p className="text-gray-400 text-sm">
                    Seul screener francophone couvrant Vietnam, Inde, Philippines et Indonésie avec données fondamentales complètes.
                  </p>
                </div>
              </div>
              <div className="flex gap-3">
                <Link to="/markets">
                  <Button variant="outline" className="border-white/20 text-white hover:bg-white/5">
                    Voir les marchés
                  </Button>
                </Link>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* FAQ Section */}
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-8">
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-2">
              Questions fréquentes
            </h2>
            <p className="text-gray-400">
              Tout ce que vous devez savoir sur VALORAMAP
            </p>
          </div>

          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq, index) => (
              <AccordionItem 
                key={index} 
                value={`item-${index}`}
                className="bg-white/5 border border-white/10 rounded-xl px-6 data-[state=open]:bg-white/[0.07]"
              >
                <AccordionTrigger className="text-white hover:no-underline py-4 text-left">
                  <span className="flex items-center gap-3">
                    <HelpCircle className="w-5 h-5 text-emerald-400 shrink-0" />
                    {faq.question}
                  </span>
                </AccordionTrigger>
                <AccordionContent className="text-gray-400 pb-4 pl-8">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>

        {/* Final CTA */}
        <div className="text-center mt-16">
          <h3 className="text-xl font-bold text-white mb-4">
            Prêt à explorer les marchés émergents ?
          </h3>
          <a 
            href={THRIVECART_URLS.trial}
            className="inline-flex items-center justify-center bg-emerald-500 hover:bg-emerald-600 text-white px-8 h-12 rounded-md font-medium transition-colors"
          >
            Commencer l'essai à 9 CHF
            <ArrowRight className="w-5 h-5 ml-2" />
          </a>
          <p className="text-sm text-gray-500 mt-3">
            7 jours d'accès complet. Remboursé si non satisfait.
          </p>
        </div>
      </div>
    </div>
  );
};

export default PricingPage;
